/** @jsx jsx */
import React, { useEffect, useRef, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { jsx, css } from '@emotion/react';
import { notify } from '../../util/notifications';
import CallVisibility from '../../components/dispatchComponents/dashboard/callVisibility';
import TruckVisibility from '../../components/dispatchComponents/dashboard/truckVisibility';
import { getCallList, getTruckList, invoiceClearOrCancel, forceLogout } from '../../actions/dispatchAction/dispatchCallsAction';
import { isManagement, getAccessLevelsByModuleName } from '../../util/customMethods';

const POLL_INTERVAL = 15000;
const CLEAR_CANCEL = ['Cleared', 'Cancel'];
const ASSIGN = ['Assign', 'Reassign'];

const pageHeader = css`
    margin-bottom: 12px;
    & h4 { font-size: 1.15rem; font-weight: 600; color: #2c3e50; margin: 0;
           display: flex; align-items: center; gap: 8px; }
    & h4 i { color: #5a8bb5; font-size: 1rem; }
`;

const container = css`
    background: #fff; border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08); padding: 16px;
`;

const unauthorized = css`
    text-align: center; padding: 60px 20px; color: #95a5a6;
    & i { font-size: 2.5rem; margin-bottom: 12px; }
    & h4 { color: #e74c3c; }
`;

const DispatchDashboard = ({ dispatch, history, refNotify, callList, truckList }) => {
    const isMgmt = isManagement();
    const intervalRef = useRef(null);

    const refreshData = useCallback(() => {
        dispatch(getCallList());
        if (!isMgmt) dispatch(getTruckList());
    }, [dispatch, isMgmt]);

    useEffect(() => {
        document.title = 'Dispatch Dashboard';
        refreshData();
        intervalRef.current = setInterval(refreshData, POLL_INTERVAL);
        return () => clearInterval(intervalRef.current);
    }, [refreshData]);

    const handleCallAction = useCallback(async (req) => {
        if (CLEAR_CANCEL.includes(req.status)) {
            const res = await dispatch(invoiceClearOrCancel(req));
            if (res.statusCode === 200) {
                notify.createNotification('success', res.statusMsg, refNotify);
                dispatch(getCallList());
            } else {
                notify.createNotification('error', res.statusMsg, refNotify);
            }
        } else if (ASSIGN.includes(req.status)) {
            history.push('/dispatch/assign/' + req.invoiceId);
        }
    }, [dispatch, history, refNotify]);

    const handleRouteInvoice = useCallback((id) => {
        history.push('/dispatch/serviceCallInfo/' + id);
    }, [history]);

    const handleForceLogout = useCallback(async (driverId, truckId) => {
        if (!window.confirm('Are you sure you wish to logout this driver?')) return;
        const res = await dispatch(forceLogout(driverId, truckId));
        if (res.statusCode === 200) {
            dispatch(getTruckList());
            notify.createNotification('success', res.statusMsg, refNotify);
        } else {
            notify.createNotification('error', res.statusMsg, refNotify);
        }
    }, [dispatch, refNotify]);

    const accessLevels = getAccessLevelsByModuleName('Dispatcher Dashboard');
    const dashboard = (Array.isArray(accessLevels) ? accessLevels[0] : accessLevels) || {};
    const accessLoaded = Object.keys(dashboard.basicAccess || {}).length > 0;
    const canView = dashboard.basicAccess?.view;
    const pageAccess = (page) => dashboard.pageLvlAccess?.[page]?.view || false;

    if (!accessLoaded) {
        return (
            <div css={container}>
                <div css={unauthorized}>
                    <i className="fa fa-spinner fa-spin"></i>
                    <h4>Loading...</h4>
                    <p>Loading your permissions, please wait.</p>
                </div>
            </div>
        );
    }

    if (!canView) {
        return (
            <div css={container}>
                <div css={unauthorized}>
                    <i className="fa fa-lock"></i>
                    <h4>Not Authorized</h4>
                    <p>You do not have permission to view the Dispatch Dashboard.</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div css={pageHeader}>
                <h4><i className="fa fa-th-large"></i> Dispatch Dashboard</h4>
            </div>
            <div css={container}>
                <CallVisibility
                    data={callList || []}
                    routeInv={handleRouteInvoice}
                    onCallActionBtnClick={handleCallAction}
                    canClear={pageAccess('Clear Call')}
                    canAssign={pageAccess('Assign/Re-Assign Call')}
                    canCancel={pageAccess('Cancel Call')}
                />
                {!isMgmt && (
                    <TruckVisibility
                        data={truckList || []}
                        onForceLogout={handleForceLogout}
                        canLogout={pageAccess('Force Log Out')}
                    />
                )}
            </div>
        </>
    );
};

const mapStateToProps = (state) => ({
    refNotify: state.refNotify,
    callList: state.dispatchCalls.callList,
    truckList: state.dispatchCalls.truckList
});

export default withRouter(connect(mapStateToProps)(DispatchDashboard));
